<script setup>
import { ref } from 'vue';
import AiAgentContentCreatorAvatar from "../../agents/content-creator/avatar.vue";

const props = defineProps({
    flowieHandoff: { type: Object, default: null },
});

const emit = defineEmits(['complete']);

// Prefilled from Flowie when the user arrived from an action plan. Nothing is
// submitted for them — they review and confirm as usual.
const handoff = props.flowieHandoff ?? {};

const contentType = ref(handoff.content_type || 'linkedin');
const topic = ref(handoff.topic || '');
const tone = ref(handoff.tone || 'professional');
const audience = ref(handoff.audience || '');
const language = ref(handoff.language || '');
const isLoading = ref(false);
const showFlowieNote = ref(Boolean(props.flowieHandoff));

const contentTypes = [
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'blog', label: 'Blog Article' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'other', label: 'Other' },
];

const tones = [
    { key: 'friendly', label: () => _mft('content-creator:onboarding.tone.friendly') },
    { key: 'professional', label: () => _mft('content-creator:onboarding.tone.professional') },
    { key: 'bold', label: () => _mft('content-creator:onboarding.tone.bold') },
];

const submit = () => {
    if (!topic.value.trim()) return;
    isLoading.value = true;

    emit('complete', {
        content_type: contentType.value,
        topic: topic.value,
        tone: tone.value,
        audience: audience.value || null,
        language: language.value || null,
    });
};

const resetLoading = () => {
    isLoading.value = false;
};

defineExpose({ resetLoading });
</script>

<template>
    <div class="flex flex-col h-full">
        <div class="flex-1 overflow-y-auto p-5">
            <div class="text-center py-4 mb-4">
                <div class="flex items-center justify-center w-12 h-12 mx-auto mb-3 rounded-full bg-aiAgentContentCreator">
                    <ai-agent-content-creator-avatar class="w-full h-full" />
                </div>
                <h2 class="text-lg font-semibold text-gray-900">
                    {{ _mft('content-creator:onboarding.title') }}
                </h2>
                <p class="text-sm text-gray-500 mt-1">
                    {{ _mft('content-creator:onboarding.subtitle') }}
                </p>
            </div>

            <flowie-context-note
                v-if="showFlowieNote"
                :dismissible="false"
            />

            <!-- Content type selection -->
            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ _mft('content-creator:onboarding.contentType') }}
                </label>
                <div class="flex flex-wrap gap-2">
                    <component
                        v-for="ct in contentTypes"
                        :key="ct.key"
                        :is="contentType === ct.key ? 'button-primary' : 'button-default'"
                        @click="contentType = ct.key"
                        size="xs"
                    >
                        {{ ct.label }}
                    </component>
                </div>
            </div>

            <!-- Topic -->
            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                    {{ _mft('content-creator:onboarding.topic') }} *
                </label>
                <textarea
                    v-model="topic"
                    rows="3"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentContentCreator focus:border-aiAgentContentCreator"
                    :placeholder="_mft('content-creator:onboarding.topicPlaceholder')"
                ></textarea>
            </div>

            <!-- Tone -->
            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ _mft('content-creator:onboarding.toneLabel') }}
                </label>
                <div class="flex gap-2">
                    <component
                        v-for="t in tones"
                        :key="t.key"
                        :is="tone === t.key ? 'button-primary' : 'button-default'"
                        @click="tone = t.key"
                        size="md"
                        class="flex-1 justify-center"
                    >
                        {{ t.label() }}
                    </component>
                </div>
            </div>

            <!-- Audience -->
            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                    {{ _mft('content-creator:onboarding.audience') }}
                </label>
                <input
                    v-model="audience"
                    type="text"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentContentCreator focus:border-aiAgentContentCreator"
                    :placeholder="_mft('content-creator:onboarding.audiencePlaceholder')"
                />
            </div>

            <!-- Language -->
            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                    {{ _mft('content-creator:onboarding.language') }}
                </label>
                <input
                    v-model="language"
                    type="text"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentContentCreator focus:border-aiAgentContentCreator"
                    :placeholder="_mft('content-creator:onboarding.languagePlaceholder')"
                />
            </div>
        </div>

        <!-- Submit button fixed at bottom -->
        <div class="flex-shrink-0 p-5 pt-3 border-t border-gray-200">
            <button-base
                @click="submit"
                style-type="primary"
                :disabled="!topic.trim() || isLoading"
                size="xl"
                class="w-full justify-center"
            >
                <template #icon v-if="isLoading">
                    <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                </template>
                {{ _mft('content-creator:onboarding.create') }}
            </button-base>
        </div>
    </div>
</template>
